import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { Label } from "@/ui/label";
import { Alert } from "@/ui/alert";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from "../../../../../constants/eventDataTypes";
import {
  UPDATE_PASSWORD_MESSAGE,
  PASSWORD_NOT_MATCH,
  PASSWORD_MESSAGES,
} from "../../../../../constants/notificationMessages";
import {
  getUserByToken,
  getPasswordPolicy,
  changePassword,
} from "../../Services/LoginService";
import { buildNotification, GenerateNotification } from "smart-react";

interface NewPasswordProps {
  setIsLoader: (v: boolean) => void;
}

type FormValues = {
  password: string;
  cpassword: string;
};

const NewPassword: React.FC<NewPasswordProps> = ({ setIsLoader }) => {
  const navigate = useNavigate();

  // alert state (compact one-line)
  const [alertMessage, setAlertMessage] = React.useState<{
    alert: boolean;
    message: string | null;
    type: "danger" | "success";
  }>({
    alert: false,
    message: null,
    type: "danger",
  });

  // token from query string
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("token");

  // user + policy
  const [userData, setUserData] = React.useState<any>({});
  const [policyPayload, setPolicyPayload] = React.useState<any>({});

  // RHF hook
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: "onSubmit",
  });

  // Legacy-equivalent password validation using dynamic policy
  const validatePassword = (value: string) => {
    if (!value) {
      return PASSWORD_MESSAGES.REQUIRED;
    }

    // If policy regex exists, try it first
    try {
      if (policyPayload?.Regex) {
        const regexPattern = new RegExp(policyPayload.Regex);
        if (regexPattern.test(value)) {
          return true;
        }
      }
    } catch {
      // ignore regex errors and fall back to manual checks
    }

    // Manual checks (mirror legacy fallbacks)
    if (value.length < 8) {
      return PASSWORD_MESSAGES.MIN_LENGTH;
    }
    if (!/[A-Z]/.test(value)) {
      return PASSWORD_MESSAGES.UPPERCASE;
    }
    if (!/\d/.test(value)) {
      return PASSWORD_MESSAGES.DIGIT;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
      return PASSWORD_MESSAGES.SPECIAL_CHAR;
    }

    // All good
    return true;
  };

  const validateConfirmPassword = (value: string) => {
    const password = getValues("password");
    if (value !== password) {
      return PASSWORD_NOT_MATCH?.title || "Passwords do not match";
    }
    return true;
  };

  // Validate user token on mount
  const validateUserToken = async (tk: string | null) => {
    if (!tk) return;
    const dataItem = {
      AppId: process.env.APP_ID,
      Token: tk,
    };
    try {
      const response = await getUserByToken({ data: dataItem });
      if (!response?.IsSuccess) {
        setAlertMessage({
          alert: true,
          type: "danger",
          message: response?.StatusDesc || "Invalid token",
        });
      } else {
        setUserData(response?.Payload);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // Fetch password policy once user data is available
  const checkPasswordPolicy = async (gatewayId: any) => {
    if (!gatewayId) return;
    try {
      const response = await getPasswordPolicy({ gatewayId });
      if (response?.IsSuccess) {
        setPolicyPayload(response?.Payload);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  React.useEffect(() => {
    validateUserToken(token);
  }, []);

  React.useEffect(() => {
    if (userData?.AuthGatewayId) {
      checkPasswordPolicy(userData?.AuthGatewayId);
    }
  }, [userData]);

  const onSubmit = async ({ password, cpassword }: FormValues) => {
    setIsLoader(true);
    const dataItem = {
      NewPassword: password,
      ConfirmPassword: cpassword,
      AppId: process.env.APP_ID,
      Token: token,
      Active: true,
    };

    try {
      const response = await changePassword({ data: dataItem });
      if (!response?.IsSuccess) {
        setAlertMessage({
          alert: true,
          type: "danger",
          message: response?.StatusDesc || "Password update failed",
        });
      } else {
        // Fire notification and go to login
        GenerateNotification(
          buildNotification(UPDATE_PASSWORD_MESSAGE),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION
        );
        setAlertMessage({
          alert: true,
          type: "success",
          message: UPDATE_PASSWORD_MESSAGE?.description,
        });
        navigate("/login");
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setAlertMessage({
        alert: true,
        type: "danger",
        message: "Something went wrong while updating password.",
      });
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {alertMessage?.alert && (
        <Alert
          variant={alertMessage?.type === "danger" ? "destructive" : "default"}
          role={alertMessage?.type === "danger" ? "alert" : "status"}
          className="p-2"
        >
          <div className="flex items-center gap-2 text-sm min-w-0">
            {alertMessage?.type === "danger" ? (
              <AlertCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
            )}
            <span className="truncate" title={alertMessage?.message ?? ""}>
              {alertMessage?.message}
            </span>
          </div>
        </Alert>
      )}

      {/* Password */}
      <div className="space-y-1.5">
        <Label htmlFor="password" className="font-medium text-foreground">
          Password
        </Label>
        <Input
          id="password"
          type="password"
          aria-invalid={errors.password ? "true" : "false"}
          aria-describedby={errors.password ? "password-error" : undefined}
          {...register("password", { validate: validatePassword })}
        />
        {errors.password && (
          <p id="password-error" className="text-sm text-destructive">
            {errors.password.message as string}
          </p>
        )}
      </div>

      {/* Confirm Password */}
      <div className="space-y-1.5">
        <Label htmlFor="cpassword" className="font-medium text-foreground">
          Confirm Password
        </Label>
        <Input
          id="cpassword"
          type="password"
          aria-invalid={errors.cpassword ? "true" : "false"}
          aria-describedby={errors.cpassword ? "cpassword-error" : undefined}
          {...register("cpassword", { validate: validateConfirmPassword })}
        />
        {errors.cpassword && (
          <p id="cpassword-error" className="text-sm text-destructive">
            {errors.cpassword.message as string}
          </p>
        )}
      </div>

      <div className="mt-6">
        <Button
          type="submit"
          fullWidth
          size="lg"
          variant="primary"
          disabled={isSubmitting}
        >
          Create Password
        </Button>
      </div>
    </form>
  );
};

export default NewPassword;
